import { useEffect, useState } from 'react';
import { Navigate, NavLink, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { Menu, Search } from 'lucide-react';
import { VISUAL_STYLE, useStore } from '@/state/store';
import { useBrandText } from '@/components/brand';
import { initUsageAnalytics } from '@/analytics/usage';
import { initPlayHistory } from '@/data/playHistory';
import { startTuneDefaultsSync } from '@/data/tuneDefaults';
import { startAccountSync } from '@/data/accountSync';
import { startSharedPlaylistSync } from '@/data/sharedPlaylistSync';
import { startCrossTabSync } from '@/state/crossTab';
import { soloedLevels } from '@/audio/levels';
import { getChordPlayer } from '@/audio/player';
import { APP_ROUTES, LEGACY_ROUTES, isWorkbenchRoute } from '@/navigation';
import { InspectorProvider } from '@/components/inspector';
import { CommandPalette } from '@/components/CommandPalette';
import { AbcLink } from '@/components/AbcLink';
import { openPalette } from '@/components/paletteBus';
import { useOwnHeight } from '@/components/useOwnHeight';
import { useWakeLock, useWakeLockStatus } from '@/components/wakeLock';
import { useKeepAwakeFallback } from '@/components/keepAwakeFallback';
import { NavDrawer } from '@/components/NavDrawer';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';
import { SignInGate } from '@/components/SignInGate';
import { MiniPlayer } from '@/components/MiniPlayer';
import { useMiniPlayerShown } from '@/components/miniPlayerShown';
import { PlaylistAddedToast } from '@/components/PlaylistAddedToast';
import { useMusicAccess } from '@/auth/musicAccess';
import { LibraryPage } from '@/pages/LibraryPage';
import { ProgressionsPage } from '@/pages/ProgressionsPage';
import { ChaptersPage } from '@/pages/ChaptersPage';
import { SequenceBuilderPage } from '@/pages/SequenceBuilderPage';
import { HomePage } from '@/pages/HomePage';
import { PlaylistsPage } from '@/pages/PlaylistsPage';
import { SharedSetlistPage } from '@/pages/SharedSetlistPage';
import { AdminPage } from '@/pages/AdminPage';
import { FeedbackPage } from '@/pages/FeedbackPage';
import { PrivacyPage } from '@/pages/PrivacyPage';
import { JoinPage } from '@/pages/JoinPage';
import { MetronomePage } from '@/pages/MetronomePage';
import { MelodyPage } from '@/pages/MelodyPage';
import { LabPage } from '@/pages/LabPage';
import { ScorePage } from '@/pages/ScorePage';
import { CorpusPage } from '@/pages/CorpusPage';
import { ArticleSegnoPage } from '@/pages/ArticleSegnoPage';
import { LearnPage } from '@/pages/LearnPage';
import { GuitarStartPage } from '@/pages/GuitarStartPage';
import { GuidePage } from '@/pages/GuidePage';
import { Drop2IntroPage } from '@/pages/Drop2IntroPage';
import { FourNoteVoicingsPage } from '@/pages/FourNoteVoicingsPage';
import { Drop3IntroPage } from '@/pages/Drop3IntroPage';
import { ScaleHarmonyPage } from '@/pages/ScaleHarmonyPage';
import './mobileTopbar.css';

/**
 * Everything focus mode slides away. Listed once: the CSS collapses these and
 * the focusin handler restores the chrome when a keyboard reaches one.
 */
const FOCUS_CHROME_SELECTOR = '.topbar, .transport-bar, .chart-footer, .tune-workspace-header';

function RouteRedirect({ to, param }: { to: string; param?: 'chapterId' | 'exhibitId' }) {
  const location = useLocation();
  const params = useParams();
  const value = param ? params[param] : undefined;
  const pathname = value ? `${to}/${encodeURIComponent(value)}` : to;

  return <Navigate replace to={{ pathname, search: location.search, hash: location.hash }} />;
}

export function App() {
  const theme = useStore((s) => s.theme);
  const brand = useBrandText();
  const location = useLocation();
  const sequenceWorkspace = isWorkbenchRoute(location.pathname);
  // The home page carries its own tune search, so the top bar's search
  // field shrinks to the round icon there and the page has one field.
  const homePage = location.pathname.replace(/\/+$/, '') === '';
  const tuneWorkspace =
    sequenceWorkspace ||
    location.pathname.replace(/\/+$/, '') === APP_ROUTES.scoreStudio ||
    location.pathname.replace(/\/+$/, '') === LEGACY_ROUTES.score;
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const focusMode = useStore((s) => s.focusMode);
  const setFocusMode = useStore((s) => s.setFocusMode);
  const keepAwake = useStore((s) => s.keepAwake);
  const wakeStatus = useWakeLockStatus();
  const musicAccess = useMusicAccess();
  const miniPlayer = useMiniPlayerShown() && musicAccess === 'open';

  // A chart on a music stand must not sleep mid-tune: hold a screen wake lock
  // while the app is visible (the platform releases it whenever the tab is
  // hidden, so it only spends battery while music is actually on screen).
  useWakeLock(keepAwake);
  // iOS DECLINES the lock in Low Power Mode, but never sleeps the screen
  // during media playback — the declined branch falls back to a tiny silent
  // looping video (the NoSleep technique; see keepAwakeFallback.ts).
  useKeepAwakeFallback(keepAwake && wakeStatus === 'declined');

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  useEffect(() => {
    setMobileNavOpen(false);
    // Each workspace opens with its document heading visible. Retaining the
    // previous page's scroll can put the tune title behind the sticky app bar.
    // Query and in-score changes keep their position, including playback.
    if (tuneWorkspace) window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname, tuneWorkspace]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const close = (event: PointerEvent) => {
      const target = event.target as Element | null;
      if (!target?.closest('.nav-drawer-trigger, .nav-drawer-panel')) {
        setMobileNavOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileNavOpen(false);
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [mobileNavOpen]);

  // Focus mode slides the header away, and the space it occupied has to go
  // with it or the sheet keeps a blank band at the top — see useOwnHeight.
  const measureTopbar = useOwnHeight('--topbar-height', '--topbar-height');

  // Two ways back for a keyboard: Escape, and simply tabbing to the chrome.
  // The slid-away header and transport stay in the tab order deliberately —
  // `inert` would hide them — so reaching one brings it back into view
  // instead of focusing something off screen. A pointer has the close in the
  // top-right corner (FocusControls). One document listener covers both bars; the transport is rendered
  // by the page, out of this component's reach.
  useEffect(() => {
    if (!focusMode) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setFocusMode(false);
    };
    const onFocusIn = (event: FocusEvent) => {
      const target = event.target;
      if (target instanceof Element && target.closest(FOCUS_CHROME_SELECTOR)) {
        setFocusMode(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('focusin', onFocusIn);
    };
  }, [focusMode, setFocusMode]);

  // Leaving the workspace must not carry focus mode to a page that has no
  // way back out of it — that would be a trapdoor out of the app's own
  // navigation.
  useEffect(() => {
    if (!sequenceWorkspace && focusMode) setFocusMode(false);
  }, [sequenceWorkspace, focusMode, setFocusMode]);

  // Pilot usage events (#324): signed_in on activation, play-minutes off the
  // shared player's transport, flush on hide. A no-op for everyone who is not
  // an activated pilot member.
  useEffect(() => {
    initUsageAnalytics(getChordPlayer());
  }, []);

  // Your play history: the band's play time, counted against the tune it plays.
  useEffect(() => initPlayHistory(getChordPlayer()), []);

  // House defaults (the owner's settings for a tune, saved for everyone):
  // refresh the device's cache now and when the app comes back into view.
  useEffect(() => startTuneDefaultsSync(), []);

  // Your saved tunes and tune settings follow you between devices.
  useEffect(() => startAccountSync(), []);
  // Shared playlists stay in step with everyone else holding the link.
  useEffect(() => startSharedPlaylistSync(), []);
  // …and between two tabs on this device, which share one localStorage.
  useEffect(() => startCrossTabSync(), []);

  // The mixer's Master channel reaches the shared player from here rather
  // than from the band hook: reverb and compression colour EVERYTHING the
  // player sounds — a tapped grip on the Library page as much as the band —
  // and the player remembers them before its context exists.
  const masterReverb = useStore((s) => (s.masterReverbEnabled ? s.masterReverb : 0));
  const masterCompression = useStore((s) => (s.masterCompressionEnabled ? s.masterCompression : 0));
  useEffect(() => {
    getChordPlayer().setMasterEffects({ reverb: masterReverb, compression: masterCompression });
  }, [masterReverb, masterCompression]);
  // The faders too: a channel's level applies to the band wherever it plays.
  const metronomeSettings = useStore((s) => s.metronomeSettings);
  useEffect(() => {
    getChordPlayer().setMetronome(metronomeSettings);
  }, [metronomeSettings]);
  const channelLevels = useStore((s) => s.channelLevels);
  const soloChannel = useStore((s) => s.soloChannel);
  useEffect(() => {
    getChordPlayer().setChannelLevels(soloedLevels(channelLevels, soloChannel));
  }, [channelLevels, soloChannel]);
  // Guitar previews also live outside the band pages. Restore their sound
  // on a direct visit to Library or Progressions, before the first tap.
  const guitarSound = useStore((s) => s.guitarSound);
  useEffect(() => {
    getChordPlayer().setGuitarSound(guitarSound);
  }, [guitarSound]);

  // iOS grants audio activation most reliably on touchend/click, but strums
  // start on pointerdown — so unlock the audio session on every plain tap.
  // Once running this is a no-op, and it also recovers the context after an
  // iOS "interrupted" state (phone call, lock screen).
  useEffect(() => {
    const unlock = () => void getChordPlayer().resume();
    window.addEventListener('touchend', unlock, { passive: true });
    window.addEventListener('click', unlock, { passive: true });
    return () => {
      window.removeEventListener('touchend', unlock);
      window.removeEventListener('click', unlock);
    };
  }, []);

  return (
    <InspectorProvider>
      <div
        className={`app-shell${sequenceWorkspace ? ' sequence-workspace-shell' : ''}${homePage ? ' home-shell' : ''}`}
        data-chart-font={VISUAL_STYLE}
        data-branded-bar=""
        data-realbook=""
        data-focus-mode={focusMode ? 'on' : undefined}
      >
        <header
          className="topbar"
          ref={measureTopbar}
          data-tune-workspace={tuneWorkspace || undefined}
        >
          <button
            type="button"
            className="nav-drawer-trigger"
            aria-label={mobileNavOpen ? 'Close navigation' : 'Open navigation'}
            aria-controls="nav-drawer-panel"
            aria-expanded={mobileNavOpen}
            onClick={() => setMobileNavOpen((open) => !open)}
          >
            <Menu size={19} strokeWidth={2} aria-hidden="true" />
          </button>
          <NavLink to={APP_ROUTES.home} className="brand" aria-label={brand.homeLabel}>
            <span className="mark brand-wordmark" aria-hidden="true">
              {brand.wordmark}
            </span>
            <span className="mark brand-logo" aria-hidden="true" />
            {brand.sub ? <span className="sub">{brand.sub}</span> : null}
          </NavLink>
          {/* Workbench pages PORTAL their tune actions here (Tunes / New /
              Export) — the header is contextual like YouTube's, so the verbs
              exist only where they mean something. */}
          {sequenceWorkspace && <div id="topbar-tune-actions" className="topbar-tune-actions" />}
          <div className="topbar-spacer" />
          {musicAccess === 'open' && (
            <button
              type="button"
              className="palette-trigger"
              data-testid="palette-trigger"
              aria-label="Open a tune or search"
              aria-keyshortcuts="Meta+K Control+K"
              title="Open a tune or search"
              onClick={() => openPalette()}
            >
              <Search
                className="palette-trigger-icon"
                size={20}
                strokeWidth={2}
                aria-hidden="true"
              />
              <span className="palette-trigger-label">Search…</span>
              <kbd>⌘K</kbd>
            </button>
          )}
        </header>
        <NavDrawer open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />

        {/* The palette lists tunes, so it waits for the gate too. */}
        {musicAccess === 'open' && <CommandPalette />}
        {/* An ABC link (`?abc=`) opens music, so it is read once the gate is
            open; until then its address waits in the URL sign-in returns to. */}
        {musicAccess === 'open' && <AbcLink />}

        <main className={`content${miniPlayer ? ' has-mini-player' : ''}`}>
          <RouteErrorBoundary resetKey={location.pathname}>
            <SignInGate>
              <Routes>
                <Route path={APP_ROUTES.home} element={<HomePage />} />
                <Route
                  path={LEGACY_ROUTES.tunes}
                  element={<RouteRedirect to={APP_ROUTES.home} />}
                />
                <Route path={APP_ROUTES.playlists} element={<PlaylistsPage />} />
                <Route path={`${APP_ROUTES.playlists}/:playlistId`} element={<PlaylistsPage />} />
                <Route
                  path={`${APP_ROUTES.sharedSetlist}/:shareId`}
                  element={<SharedSetlistPage />}
                />
                <Route path={APP_ROUTES.admin} element={<AdminPage />} />
                <Route path={APP_ROUTES.feedback} element={<FeedbackPage />} />
                <Route path={APP_ROUTES.privacy} element={<PrivacyPage />} />
                <Route path={`${APP_ROUTES.join}/:token`} element={<JoinPage />} />
                <Route path={APP_ROUTES.metronome} element={<MetronomePage />} />
                <Route path={APP_ROUTES.workbench} element={<SequenceBuilderPage />} />
                <Route path={APP_ROUTES.scoreStudio} element={<ScorePage />} />

                <Route path={APP_ROUTES.learn} element={<LearnPage />} />
                <Route path={APP_ROUTES.learnStart} element={<GuitarStartPage />} />
                <Route path={APP_ROUTES.guide} element={<GuidePage />} />
                <Route path={APP_ROUTES.fourNoteVoicings} element={<FourNoteVoicingsPage />} />
                <Route path={APP_ROUTES.drop2Intro} element={<Drop2IntroPage />} />
                <Route path={APP_ROUTES.drop3Intro} element={<Drop3IntroPage />} />
                <Route path={APP_ROUTES.studies} element={<ChaptersPage />} />
                <Route path={`${APP_ROUTES.studies}/:chapterId`} element={<ChaptersPage />} />
                <Route path={APP_ROUTES.progressions} element={<ProgressionsPage />} />
                <Route path={APP_ROUTES.chordLibrary} element={<LibraryPage />} />
                <Route path={APP_ROUTES.scaleHarmony} element={<ScaleHarmonyPage />} />
                <Route path={APP_ROUTES.topNoteFinder} element={<MelodyPage />} />
                <Route path={APP_ROUTES.lab} element={<LabPage />} />
                <Route path={`${APP_ROUTES.lab}/:exhibitId`} element={<LabPage />} />
                <Route path={APP_ROUTES.corpus} element={<CorpusPage />} />
                <Route path={APP_ROUTES.articleSegno} element={<ArticleSegnoPage />} />

                <Route
                  path={LEGACY_ROUTES.sequence}
                  element={<RouteRedirect to={APP_ROUTES.workbench} />}
                />
                <Route
                  path={LEGACY_ROUTES.score}
                  element={<RouteRedirect to={APP_ROUTES.scoreStudio} />}
                />
                <Route
                  path={LEGACY_ROUTES.drop2Intro}
                  element={<RouteRedirect to={APP_ROUTES.drop2Intro} />}
                />
                <Route
                  path={LEGACY_ROUTES.chapters}
                  element={<RouteRedirect to={APP_ROUTES.studies} />}
                />
                <Route
                  path={`${LEGACY_ROUTES.chapters}/:chapterId`}
                  element={<RouteRedirect to={APP_ROUTES.studies} param="chapterId" />}
                />
                <Route
                  path={LEGACY_ROUTES.progressions}
                  element={<RouteRedirect to={APP_ROUTES.progressions} />}
                />
                <Route
                  path={LEGACY_ROUTES.chordLibrary}
                  element={<RouteRedirect to={APP_ROUTES.chordLibrary} />}
                />
                <Route
                  path={LEGACY_ROUTES.melody}
                  element={<RouteRedirect to={APP_ROUTES.topNoteFinder} />}
                />
                <Route path={LEGACY_ROUTES.lab} element={<RouteRedirect to={APP_ROUTES.lab} />} />
                <Route
                  path={`${LEGACY_ROUTES.lab}/:exhibitId`}
                  element={<RouteRedirect to={APP_ROUTES.lab} param="exhibitId" />}
                />
                <Route
                  path={LEGACY_ROUTES.corpus}
                  element={<RouteRedirect to={APP_ROUTES.corpus} />}
                />
                <Route
                  path="/learn/tools/top-note-finder"
                  element={<RouteRedirect to={APP_ROUTES.topNoteFinder} />}
                />
                <Route path="*" element={<Navigate to={APP_ROUTES.workbench} replace />} />
              </Routes>
            </SignInGate>
          </RouteErrorBoundary>
        </main>
        {miniPlayer && <MiniPlayer />}
        <PlaylistAddedToast />
      </div>
    </InspectorProvider>
  );
}
